import React, { useEffect, useState, useRef } from 'react';
import Layout from '../components/layout/Layout';
import SectionTitle from '../components/shared/SectionTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { companyInfo, allServices } from '../data/mock';
import { Send, Phone, Mail, MapPin, Check, Upload, X, Image, Camera } from 'lucide-react';
import { toast } from 'sonner';

const PreventivePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    message: '',
    interestedInInstallments: false
  });
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceSelect = (serviceId) => {
    setFormData(prev => ({ ...prev, service: serviceId }));
  };

  // Check if selected service requires photo upload option
  const showPhotoUpload = formData.service === 'pergotende' || formData.service === 'bioclimatiche';

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addPhotos(files);
  };

  // Add photos with validation
  const addPhotos = (files) => {
    const validFiles = files.filter(file => {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} non è un'immagine valida`);
        return false;
      }
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} è troppo grande (max 10MB)`);
        return false;
      }
      return true;
    });

    // Limit to 5 photos
    const remainingSlots = 5 - photos.length;
    if (validFiles.length > remainingSlots) {
      toast.warning(`Puoi caricare massimo 5 foto. Aggiunte solo ${remainingSlots}.`);
    }

    const newPhotos = validFiles.slice(0, remainingSlots).map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setPhotos(prev => [...prev, ...newPhotos]);
  };

  // Remove a photo
  const removePhoto = (index) => {
    setPhotos(prev => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    addPhotos(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      // Create FormData for multipart submission
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('address', formData.address || '');
      submitData.append('service', formData.service);
      submitData.append('message', formData.message || '');
      submitData.append('interested_in_installments', formData.interestedInInstallments ? 'true' : 'false');
      
      // Append photos if any
      photos.forEach((photo) => {
        submitData.append('photos', photo.file);
      });
      
      const response = await fetch(`${API_URL}/api/quote`, {
        method: 'POST',
        body: submitData
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Richiesta inviata con successo!');
      } else {
        throw new Error(result.detail || 'Errore nell\'invio');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Si è verificato un errore. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      photos.forEach(photo => URL.revokeObjectURL(photo.preview));
    };
  }, []);

  if (isSubmitted) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 pt-20">
          <div className="max-w-lg mx-auto text-center px-6">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-sky-500/10 flex items-center justify-center">
              <Check className="w-10 h-10 text-sky-500" />
            </div>
            <h1 className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
              Richiesta inviata con successo
            </h1>
            <p className="text-lg text-gray-600 font-light mb-8">
              Grazie per averci contattato. Ti risponderemo entro 24 ore lavorative.
              {photos.length > 0 && (
                <span className="block mt-2 text-sky-600">
                  Abbiamo ricevuto {photos.length} foto per il rendering personalizzato.
                </span>
              )}
            </p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-4 rounded-none"
            >
              Torna alla Home
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gray-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
              Richiedi un preventivo personalizzato
            </h1>
            <p className="text-lg text-gray-300 font-light">
              Compila il modulo e riceverai un preventivo dettagliato entro 24 ore. 
              Nessun impegno, solo consulenza di qualità.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Service Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-4">
                    Quale soluzione ti interessa?
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {allServices.map((service) => (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => handleServiceSelect(service.id)}
                        className={`p-4 text-sm text-left border transition-all duration-300 ${
                          formData.service === service.id
                            ? 'border-sky-500 bg-sky-50 text-sky-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {service.title}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Photo Upload Section - Only for Pergotende and Bioclimatiche */}
                {showPhotoUpload && (
                  <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-6 border border-sky-100 rounded-lg">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                        <Camera className="w-6 h-6 text-sky-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">
                          Carica foto del tuo spazio
                        </h3>
                        <p className="text-sm text-gray-600">
                          Inviaci foto del terrazzo, giardino o spazio dove desideri installare 
                          la struttura. Realizzeremo un <strong>rendering personalizzato gratuito</strong> per 
                          mostrarti come apparirà il risultato finale.
                        </p>
                      </div>
                    </div>

                    {/* Drag & Drop Zone */}
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-300 ${
                        isDragging
                          ? 'border-sky-500 bg-sky-100/50'
                          : 'border-gray-300 hover:border-sky-400 hover:bg-white'
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-sky-500' : 'text-gray-400'}`} />
                      <p className="text-gray-700 font-medium mb-1">
                        Trascina le foto qui o clicca per selezionarle
                      </p>
                      <p className="text-sm text-gray-500">
                        JPG, PNG fino a 10MB • Massimo 5 foto
                      </p>
                    </div>

                    {/* Photo Previews */}
                    {photos.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-3">
                          Foto caricate ({photos.length}/5):
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                          {photos.map((photo, index) => (
                            <div key={index} className="relative group aspect-square">
                              <img
                                src={photo.preview}
                                alt={`Foto ${index + 1}`}
                                className="w-full h-full object-cover rounded-lg border border-gray-200"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removePhoto(index);
                                }}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate rounded-b-lg">
                                {photo.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tips */}
                    <div className="mt-4 p-3 bg-white/70 rounded-lg">
                      <p className="text-xs text-gray-600 font-medium mb-2">💡 Suggerimenti per foto migliori:</p>
                      <ul className="text-xs text-gray-500 space-y-1">
                        <li>• Scatta in orizzontale con buona illuminazione</li>
                        <li>• Includi l'area dove verrà installata la struttura</li>
                        <li>• Fotografa anche elementi architettonici circostanti</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">
                      Nome e Cognome *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-gray-200 rounded-none focus:border-sky-500 focus:ring-sky-500"
                      placeholder="Mario Rossi"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                      Email *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-gray-200 rounded-none focus:border-sky-500 focus:ring-sky-500"
                      placeholder="mario@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">
                      Telefono *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-gray-200 rounded-none focus:border-sky-500 focus:ring-sky-500"
                      placeholder="+39 333 1234567"
                    />
                  </div>
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-900 mb-2">
                      Indirizzo installazione
                    </label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      value={formData.address}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-gray-200 rounded-none focus:border-sky-500 focus:ring-sky-500"
                      placeholder="Via, Città"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
                    Descrivi il tuo progetto
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-gray-200 rounded-none focus:border-sky-500 focus:ring-sky-500 resize-none"
                    placeholder="Dimensioni dello spazio, esigenze particolari, preferenze estetiche..."
                  />
                </div>

                {/* Installment Payment Option */}
                <div className="bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-lg p-4">
                  <label className="flex items-start gap-4 cursor-pointer group">
                    <div className="relative flex items-center justify-center mt-0.5">
                      <input
                        type="checkbox"
                        checked={formData.interestedInInstallments}
                        onChange={(e) => setFormData(prev => ({ ...prev, interestedInInstallments: e.target.checked }))}
                        className="peer sr-only"
                      />
                      <div className="w-6 h-6 border-2 border-sky-300 rounded bg-white peer-checked:bg-sky-500 peer-checked:border-sky-500 transition-all duration-200 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white opacity-0 peer-checked:opacity-100 transition-opacity" style={{ opacity: formData.interestedInInstallments ? 1 : 0 }} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">Sono interessato a un pagamento rateizzato</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Seleziona questa opzione se desideri ricevere informazioni sulle nostre soluzioni di <strong>finanziamento a tasso zero</strong> o altre modalità di pagamento dilazionato.
                      </p>
                    </div>
                  </label>
                </div>

                {/* Deposit Notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-amber-900 mb-1">Informazioni sul sopralluogo</h4>
                      <p className="text-sm text-amber-800">
                        <strong>Provincia di Pisa:</strong> Sopralluogo e preventivo <span className="text-green-600 font-semibold">GRATUITO</span>
                      </p>
                      <p className="text-sm text-amber-800 mt-1">
                        <strong>Altre province toscane:</strong> È richiesto un <span className="font-semibold">contributo spese di sopralluogo</span>, 
                        che verrà <strong>interamente scalato</strong> dal preventivo in caso di accettazione del lavoro.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white px-12 py-4 text-lg font-medium rounded-none transition-all duration-300 disabled:opacity-50 group"
                  >
                    {isSubmitting ? (
                      'Invio in corso...'
                    ) : (
                      <>
                        Invia richiesta
                        <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                      </>
                    )}
                  </Button>
                  {photos.length > 0 && (
                    <span className="text-sm text-gray-500 flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      {photos.length} foto allegate
                    </span>
                  )}
                </div>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 p-8 lg:p-10 sticky top-32">
                <h3 className="text-xl font-medium text-gray-900 mb-6">
                  Preferisci contattarci direttamente?
                </h3>
                <div className="space-y-6">
                  <a
                    href={`tel:+39${companyInfo.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-4 text-gray-600 hover:text-sky-500 transition-colors duration-300"
                  >
                    <div className="w-12 h-12 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-sky-500" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Telefono Fisso</div>
                      <div className="font-medium text-gray-900">{companyInfo.phone}</div>
                    </div>
                  </a>
                  <a
                    href={`https://wa.me/39${companyInfo.mobile.replace(/\s/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 text-gray-600 hover:text-green-500 transition-colors duration-300"
                  >
                    <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">WhatsApp</div>
                      <div className="font-medium text-gray-900">{companyInfo.mobile}</div>
                    </div>
                  </a>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-center gap-4 text-gray-600 hover:text-sky-500 transition-colors duration-300"
                  >
                    <div className="w-12 h-12 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-sky-500" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Email</div>
                      <div className="font-medium text-gray-900">{companyInfo.email}</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-sky-500" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Showroom</div>
                      <div className="font-medium text-gray-900">
                        {companyInfo.address.street}<br />
                        {companyInfo.address.cap} {companyInfo.address.city} ({companyInfo.address.province})
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">Orari di apertura</h4>
                  <div className="text-sm text-gray-600 space-y-1 font-light">
                    <p>{companyInfo.workingHours.weekdays}</p>
                  </div>
                </div>

                {/* Rendering Info Box */}
                <div className="mt-8 p-4 bg-sky-50 border border-sky-100 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Camera className="w-5 h-5 text-sky-500" />
                    <span className="font-medium text-gray-900 text-sm">Rendering Gratuito</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Per pergotende e bioclimatiche, carica le foto del tuo spazio e riceverai 
                    un rendering personalizzato per visualizzare il risultato finale.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PreventivePage;
